/**
 * Frontend Sentry helpers for structured error tracking.
 *
 * Mirrors the server's capture* helpers (server/src/lib/sentry.ts) using
 * @sentry/react conventions. `isExpectedUserError` is the shared predicate
 * for errors already surfaced to the user that should not become Sentry noise.
 */

import * as Sentry from "@sentry/react";
import {
  INSUFFICIENT_CREDITS_CODE,
  CONTENT_FLAGGED_CODE,
  GENERATION_TIMEOUT_CODE,
} from "@shared/schemas/enums";
import { SENTRY_TAGS, SENTRY_ERROR_TYPES } from "@/config/sentry";
import { captureEvent } from "@/lib/analyticsQueue";

/** Expected payment/coupon validation errors that should not be reported to Sentry. */
const EXPECTED_PAYMENT_ERRORS = [/coupon/i, /already.*used/i];

/**
 * True for errors already surfaced to the user via toasts that don't
 * represent unexpected crashes — rate-limit, auth (401), payment-required
 * (402), insufficient credits, content-flagged, generation-timeout, the
 * connection-lost error our API client raises after exhausting its retry,
 * and unauthenticated requests. Anything else is a real bug or backend
 * incident worth a Sentry event — including a raw transient network error
 * (e.g. a sustained Supabase/API outage), which we deliberately do NOT
 * suppress here so incidents stay alertable. Shared by the generation and
 * React Query capture helpers.
 */
export function isExpectedUserError(error: unknown): boolean {
  const e = error as {
    isRateLimited?: boolean;
    isConnectionLost?: boolean;
    status?: number;
    code?: string;
  };
  if (e?.isRateLimited) return true;
  if (e?.isConnectionLost) return true;
  if (e?.status === 401 || e?.status === 402 || e?.status === 429) return true;
  if (
    e?.code === INSUFFICIENT_CREDITS_CODE ||
    e?.code === CONTENT_FLAGGED_CODE ||
    e?.code === GENERATION_TIMEOUT_CODE
  ) {
    return true;
  }
  if (error instanceof Error && /Not authenticated/i.test(error.message)) {
    return true;
  }
  return false;
}

/**
 * Pull a stable, low-cardinality "kind" out of an error so PostHog dashboards
 * can group by it. More specific labels win — `connection_lost` is more
 * actionable than the generic `expected` bucket.
 */
function classifyError(error: unknown): string {
  const e = error as {
    isRateLimited?: boolean;
    isConnectionLost?: boolean;
    status?: number;
    code?: string;
  };
  if (e?.isConnectionLost) return "connection_lost";
  if (e?.isRateLimited) return "rate_limited";
  if (e?.code === INSUFFICIENT_CREDITS_CODE) return "insufficient_credits";
  if (e?.code === CONTENT_FLAGGED_CODE) return "content_flagged";
  if (e?.code === GENERATION_TIMEOUT_CODE) return "generation_timeout";
  if (e?.status === 401) return "unauthenticated";
  if (e?.status === 402) return "payment_required";
  if (e?.status === 429) return "rate_limited";
  if (typeof e?.status === "number" && e.status >= 500) return "server_error";
  if (typeof e?.status === "number" && e.status >= 400) return "client_error";
  if (error instanceof Error && /Not authenticated/i.test(error.message)) {
    return "unauthenticated";
  }
  return "unexpected";
}

/**
 * Fire a PostHog event for every captured error — regardless of whether
 * Sentry filters it. This closes the visibility gap that hid the
 * "Connection lost" / rate-limited / insufficient-credits failures from us:
 * Sentry stays quiet (correct — they're toasts, not alerts), but PostHog now
 * gets a structured record we can query, dashboard, and alert on.
 *
 * Shared by every `capture*Error` helper below so a future helper added to
 * this file gets the same instrumentation by going through the same call.
 */
function emitClientErrorAnalytics(
  error: unknown,
  context: {
    capture: "generation" | "bot" | "payment" | "query";
    flow?: string;
    surface?: string;
    action: string;
    endpoint?: string;
    model?: string;
  },
): void {
  const e = error as {
    isRateLimited?: boolean;
    isConnectionLost?: boolean;
    status?: number;
    code?: string;
  };
  captureEvent("client_error", {
    capture: context.capture,
    flow: context.flow,
    surface: context.surface,
    action: context.action,
    endpoint: context.endpoint,
    model: context.model,
    error_kind: classifyError(error),
    error_code: e?.code,
    status: e?.status,
    is_connection_lost: !!e?.isConnectionLost,
    is_rate_limited: !!e?.isRateLimited,
    is_expected: isExpectedUserError(error),
    online: typeof navigator !== "undefined" ? navigator.onLine : undefined,
    error_message:
      error instanceof Error ? error.message.slice(0, 200) : undefined,
  });
}

/**
 * Capture a payment-related error with structured tags.
 *
 * Skips rate-limited (429) errors and expected coupon-validation errors —
 * those are user-facing and already handled by the apiClient toast.
 * Every error (filtered or not) is mirrored to PostHog via
 * `emitClientErrorAnalytics` so we have visibility on the silent ones too.
 */
export function capturePaymentError(
  error: unknown,
  context: { endpoint: string; action: string },
): void {
  emitClientErrorAnalytics(error, {
    capture: "payment",
    action: context.action,
    endpoint: context.endpoint,
  });

  if (error && (error as { isRateLimited?: boolean }).isRateLimited) return;

  // Skip expected validation errors (e.g. coupon already used)
  const msg = error instanceof Error ? error.message : "";
  if (EXPECTED_PAYMENT_ERRORS.some((re) => re.test(msg))) return;

  Sentry.withScope((scope) => {
    scope.setTag(SENTRY_TAGS.TYPE, SENTRY_ERROR_TYPES.PAYMENT_FAILURE);
    scope.setTag(SENTRY_TAGS.ENDPOINT, context.endpoint);
    scope.setTag(SENTRY_TAGS.ACTION, context.action);
    if (error instanceof Error) scope.setExtra("message", error.message);
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}

/**
 * Capture a studio-flow generation error with structured tags.
 *
 * Skips expected user errors (see `isExpectedUserError`) from Sentry. Tags by
 * flow + action so Sentry dashboards can group generation failures per flow.
 * Every error (filtered or not) is mirrored to PostHog via
 * `emitClientErrorAnalytics` — this closes the visibility gap where
 * `isConnectionLost` and other "expected user errors" were silently
 * swallowed (the original miss in the talking-avatar mobile bug).
 *
 * `endpoint` is optional but strongly recommended at every call site — it
 * lets PostHog/Sentry dashboards distinguish e.g. /api/talking-avatar/preview
 * from /api/talking-avatar/video when the flow tag is the same.
 */
export function captureGenerationError(
  error: unknown,
  context: { flow: string; action: string; model?: string; endpoint?: string },
): void {
  emitClientErrorAnalytics(error, {
    capture: "generation",
    flow: context.flow,
    action: context.action,
    model: context.model,
    endpoint: context.endpoint,
  });

  if (isExpectedUserError(error)) return;

  Sentry.withScope((scope) => {
    scope.setTag(SENTRY_TAGS.TYPE, SENTRY_ERROR_TYPES.GENERATION_FAILURE);
    scope.setTag(SENTRY_TAGS.FLOW, context.flow);
    scope.setTag(SENTRY_TAGS.ACTION, context.action);
    if (context.model) scope.setTag(SENTRY_TAGS.MODEL, context.model);
    if (context.endpoint) scope.setTag(SENTRY_TAGS.ENDPOINT, context.endpoint);
    if (error instanceof Error) scope.setExtra("message", error.message);
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}

/**
 * Capture a bot-builder or bot-chat frontend error.
 *
 * Mirrors `captureGenerationError`: filters expected user errors (401/402/429,
 * insufficient credits, content flagged) via `isExpectedUserError`, then tags
 * by surface + action + (optional) botId so dashboards can split builder vs
 * chat failures. Every error (filtered or not) is mirrored to PostHog via
 * `emitClientErrorAnalytics`.
 */
export function captureBotError(
  error: unknown,
  context: {
    surface: "builder" | "chat";
    action: string;
    botId?: string | null;
    endpoint?: string;
  },
): void {
  emitClientErrorAnalytics(error, {
    capture: "bot",
    surface: context.surface,
    action: context.action,
    endpoint: context.endpoint,
  });

  if (isExpectedUserError(error)) return;

  const type =
    context.surface === "builder"
      ? SENTRY_ERROR_TYPES.BOT_BUILDER_FAILURE
      : SENTRY_ERROR_TYPES.BOT_CHAT_FAILURE;

  Sentry.withScope((scope) => {
    scope.setTag(SENTRY_TAGS.TYPE, type);
    scope.setTag(SENTRY_TAGS.SURFACE, context.surface);
    scope.setTag(SENTRY_TAGS.ACTION, context.action);
    if (context.botId) scope.setTag(SENTRY_TAGS.BOT_ID, context.botId);
    if (context.endpoint) scope.setTag(SENTRY_TAGS.ENDPOINT, context.endpoint);
    if (error instanceof Error) scope.setExtra("message", error.message);
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}

/**
 * Capture an unexpected React Query failure. Wired into the global
 * QueryCache / MutationCache `onError` handlers (src/lib/queryClient.ts) so
 * no query or mutation error fails silently. Expected user errors are
 * filtered from Sentry but still mirrored to PostHog.
 */
export function captureQueryError(
  error: unknown,
  context: { source: "query" | "mutation" },
): void {
  emitClientErrorAnalytics(error, {
    capture: "query",
    action: context.source,
  });

  if (isExpectedUserError(error)) return;

  Sentry.withScope((scope) => {
    scope.setTag(SENTRY_TAGS.TYPE, SENTRY_ERROR_TYPES.QUERY_FAILURE);
    scope.setTag(SENTRY_TAGS.SOURCE, context.source);
    if (error instanceof Error) scope.setExtra("message", error.message);
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}
